import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { appName, experienceDescriptions } from "@/lib/constants";
import { useOnboardingStore } from "@/store/use-onboarding-store";

type WelcomeOverlayProps = {
  forceOpen?: boolean;
  onClose?: () => void;
};

const WelcomeOverlay = ({ forceOpen = false, onClose }: WelcomeOverlayProps) => {
  const [hydrated, setHydrated] = useState(false);
  const [visible, setVisible] = useState(false);
  const hasSeenWelcome = useOnboardingStore((state) => state.hasSeenWelcome);
  const setWelcomeSeen = useOnboardingStore((state) => state.setWelcomeSeen);

  useEffect(() => {
    const unsubscribe = useOnboardingStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });

    if (useOnboardingStore.persist.hasHydrated()) {
      setHydrated(true);
    }

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (hydrated && !hasSeenWelcome) {
      setVisible(true);
    }
  }, [hydrated, hasSeenWelcome]);

  useEffect(() => {
    if (forceOpen) {
      setVisible(true);
    }
  }, [forceOpen]);

  const dismiss = () => {
    setWelcomeSeen();
    setVisible(false);
    onClose?.();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-40 flex items-center justify-center bg-[#0a0a0a]/90 backdrop-blur-xl"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="mx-4 flex max-h-screen max-w-lg flex-col items-center overflow-y-auto py-8 text-center"
            exit={{ opacity: 0, scale: 0.98, y: -10 }}
            initial={{ opacity: 0, scale: 0.97, y: 20 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            <p className="text-sm font-medium uppercase tracking-widest text-emerald-400">Interactive Prayer Globe</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">
              <span className="text-zinc-50">Explore </span>
              <span className="bg-linear-to-r from-emerald-400 to-emerald-200 bg-clip-text text-transparent">
                {appName}
              </span>
            </h2>
            <p className="mt-3 max-w-sm text-base leading-relaxed text-zinc-400">
              Follow the live prayer state across curated world cities, then focus one location to inspect today's
              schedule.
            </p>

            <div className="mt-6 flex max-h-[50vh] flex-col items-start gap-0 overflow-y-auto text-left md:mt-8">
              {experienceDescriptions.map((experience, index) => (
                <motion.div
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -10 }}
                  key={experience.title}
                  transition={{ delay: 0.4 + index * 0.08, duration: 0.3 }}
                >
                  <div className="flex flex-col items-center self-stretch">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-zinc-700 text-xs font-medium text-zinc-400">
                      {index + 1}
                    </div>
                    {index < experienceDescriptions.length - 1 && <div className="w-px flex-1 bg-zinc-800" />}
                  </div>
                  <div className="pb-5">
                    <p className="text-sm font-medium text-zinc-200">{experience.title}</p>
                    <p className="text-xs text-zinc-500">{experience.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.button
              animate={{ opacity: 1 }}
              className="mt-6 cursor-pointer rounded-full bg-emerald-500 px-8 py-3 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400"
              initial={{ opacity: 0 }}
              onClick={dismiss}
              transition={{ delay: 0.9 }}
              type="button"
            >
              Enter the Globe
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeOverlay;
